import { create } from 'zustand';
import { ensureGsiLoaded } from '../utils/googleClient';
import { showToast } from './useToastStore';

/* Gmail connection for Google services. Deliberately NO backend involvement:
   the OAuth grant happens in the browser (GIS token flow) and the result lives
   only in localStorage — nothing is written to our DB. The stored access token
   expires after ~1h; the connection (account + Google-side grant) persists, and
   getGmailAccessToken() re-requests a fresh token for that same account when
   needed, without an account chooser. */
const GMAIL_KEY = 'rp_gmail';
const SCOPES = 'openid email profile https://www.googleapis.com/auth/gmail.send';

const readStored = () => {
  try {
    return JSON.parse(localStorage.getItem(GMAIL_KEY));
  } catch {
    return null;
  }
};

const clientId = () => process.env.REACT_APP_GOOGLE_CLIENT_ID;

/* loginHint: the address of the account to use, so Google skips its account
   chooser — what made a token refresh look like "log in to Gmail again". */
function requestToken({ prompt, loginHint }) {
  return new Promise((resolve, reject) => {
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId(),
      scope: SCOPES,
      ...(loginHint ? { login_hint: loginHint } : {}),
      callback: (resp) => (resp.error ? reject(new Error(resp.error_description || resp.error)) : resolve(resp)),
      error_callback: (e) =>
        reject(Object.assign(new Error(e.message || 'Google sign-in was cancelled.'), { gsiType: e.type })),
    });
    tokenClient.requestAccessToken(prompt != null ? { prompt } : undefined);
  });
}

const useGmailStore = create((set, get) => ({
  account: readStored(),
  connecting: false,

  connect: async () => {
    if (!clientId()) {
      showToast('Set REACT_APP_GOOGLE_CLIENT_ID in .env to enable Connect Gmail');
      return;
    }
    if (get().connecting) return;
    set({ connecting: true });
    try {
      await ensureGsiLoaded();
      const resp = await requestToken({});
      const info = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${resp.access_token}` },
      }).then((r) => r.json());
      const account = {
        email: info.email,
        name: info.name,
        picture: info.picture,
        accessToken: resp.access_token,
        expiresAt: Date.now() + Number(resp.expires_in || 3600) * 1000,
      };
      localStorage.setItem(GMAIL_KEY, JSON.stringify(account));
      set({ account });
      showToast(`Gmail connected — ${info.email} ✓`);
    } catch (err) {
      if (err.gsiType !== 'popup_closed') showToast(err.message);
    } finally {
      set({ connecting: false });
    }
  },

  disconnect: () => {
    const account = get().account;
    try {
      if (account?.accessToken && window.google?.accounts?.oauth2) {
        window.google.accounts.oauth2.revoke(account.accessToken, () => {});
      }
    } catch {
      /* revoking is best-effort — local disconnect proceeds regardless */
    }
    localStorage.removeItem(GMAIL_KEY);
    set({ account: null });
    showToast('Gmail disconnected');
  },
}));

/* A live access token for Gmail API calls. A stored token is reused until a
   minute before it expires (Google issues them for about an hour), so most
   sends never touch Google at all. Past that, Google re-issues one for the
   connected account with no account chooser or consent screen (prompt
   'none') — its window may flash and close by itself. Only if Google can't
   do that silently (signed out of that account, or access revoked) is the
   user asked, once. */
export async function getGmailAccessToken() {
  const { account } = useGmailStore.getState();
  if (!account) return null;
  if (account.accessToken && account.expiresAt - 60000 > Date.now()) return account.accessToken;
  await ensureGsiLoaded();
  let resp;
  try {
    resp = await requestToken({ prompt: 'none', loginHint: account.email });
  } catch (err) {
    if (err.gsiType === 'popup_closed') throw err; // they closed it — don't open another
    resp = await requestToken({ prompt: '', loginHint: account.email });
  }
  const refreshed = {
    ...account,
    accessToken: resp.access_token,
    expiresAt: Date.now() + Number(resp.expires_in || 3600) * 1000,
  };
  localStorage.setItem(GMAIL_KEY, JSON.stringify(refreshed));
  useGmailStore.setState({ account: refreshed });
  return refreshed.accessToken;
}

export default useGmailStore;
